import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Plus, GripVertical, Send, Inbox, CheckCircle2, AlertTriangle, Clock,
    RefreshCcw, Bell, TrendingUp,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { tr } from "@/lib/i18n";

const COLUMNS = [
    { id: "pending", label: "Pending", color: "#94A3B8", hint: "Prepared, not yet sent" },
    { id: "sent", label: "Sent", color: "#00B4D8", hint: "Delivered to the agency" },
    { id: "awaiting", label: "Awaiting Response", color: "#FFB703", hint: "With the reviewer — follow up if stale" },
    { id: "received", label: "Received", color: "#06D6A0", hint: "Comments in — review & action" },
    { id: "closed", label: "Closed", color: "#0A2540", hint: "No further action" },
];

const COLUMN_COLORS = ["#94A3B8", "#00B4D8", "#FFB703", "#06D6A0", "#0A2540", "#7B61FF", "#EF476F", "#B8860B"];

const NEXT_ACTION = {
    pending: { label: "Mark as sent", to: "sent", icon: Send },
    sent: { label: "Mark awaiting", to: "awaiting", icon: Clock },
    awaiting: { label: "Record response", to: "received", icon: Inbox },
    received: { label: "Close review", to: "closed", icon: CheckCircle2 },
};

const OVERDUE_DAYS = 21;

export default function Circulations() {
    const { tr } = useLanguage();
    const [items, setItems] = useState([]);
    const [apps, setApps] = useState([]);
    const [dragId, setDragId] = useState(null);
    const [overCol, setOverCol] = useState(null);
    const [filterApp, setFilterApp] = useState("all");
    const [showNew, setShowNew] = useState(false);
    const [form, setForm] = useState({ application_id: "", recipient: "", organization: "" });
    const [columns, setColumns] = useState(COLUMNS);

    const load = () => {
        api.get("/circulations").then((r) => setItems(r.data)).catch(() => {});
        api.get("/applications").then((r) => setApps(r.data)).catch(() => {});
        api.get("/workflows/active/all").then((r) => {
            const wf = r.data?.circulation_review;
            if (wf) {
                const nodes = [];
                const byId = Object.fromEntries(wf.nodes.map((n) => [n.id, n]));
                let cur = wf.nodes.find((n) => n.type === "start");
                const seen = new Set();
                while (cur && !seen.has(cur.id)) {
                    seen.add(cur.id);
                    if (["activity", "auto"].includes(cur.type)) nodes.push(cur);
                    const nx = wf.edges.filter((e) => e.from === cur.id);
                    cur = nx.length ? byId[nx[0].to] : null;
                }
                if (nodes.length) {
                    setColumns(nodes.map((n, i) => ({
                        id: n.status_id || n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
                        label: n.label,
                        color: COLUMN_COLORS[i % COLUMN_COLORS.length],
                        hint: n.role ? `${n.role} · v${wf.version}` : `v${wf.version}`,
                    })));
                }
            }
        }).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const visible = useMemo(
        () => (filterApp === "all" ? items : items.filter((i) => i.file_number === filterApp)),
        [items, filterApp]
    );

    const grouped = useMemo(() => {
        const g = Object.fromEntries(columns.map((c) => [c.id, []]));
        visible.forEach((i) => { if (g[i.status]) g[i.status].push(i); });
        return g;
    }, [visible, columns]);

    const stats = useMemo(() => {
        const inflight = items.filter((i) => ["sent", "awaiting"].includes(i.status));
        const overdue = inflight.filter((i) => i.days_outstanding > OVERDUE_DAYS);
        const responded = items.filter((i) => ["received", "closed"].includes(i.status));
        const avgDays = inflight.length
            ? Math.round(inflight.reduce((s, i) => s + (i.days_outstanding || 0), 0) / inflight.length)
            : 0;
        const rate = items.length ? Math.round((responded.length / items.length) * 100) : 0;
        return { inflight: inflight.length, overdue, avgDays, rate };
    }, [items]);

    const move = async (circ, newStatus) => {
        if (!newStatus || circ.status === newStatus) return;
        setItems((prev) => prev.map((i) => (i.id === circ.id ? { ...i, status: newStatus, days_outstanding: newStatus === "received" ? 0 : i.days_outstanding } : i)));
        try {
            await api.patch(`/circulations/${circ.id}`, { status: newStatus });
            toast.success(`${circ.recipient} → ${columns.find((c) => c.id === newStatus)?.label}`);
        } catch (e) {
            toast.error(formatApiError(e));
            load();
        }
    };

    const nudge = async (circ) => {
        try {
            await api.patch(`/circulations/${circ.id}`, { comments: circ.comments || "" });
            toast.success(`Reminder logged for ${circ.recipient}`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const create = async () => {
        try {
            await api.post("/circulations", form);
            toast.success(tr("Circulation added"));
            setShowNew(false);
            setForm({ application_id: "", recipient: "", organization: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const fileNumbers = [...new Set(items.map((i) => i.file_number))];

    return (
        <div data-testid="circulations-page" className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading text-navy dark:text-white">{tr("Circulations")}</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{tr("Drag cards between stages, or use the action button on each card. Items over")} {OVERDUE_DAYS} {tr("days are flagged.")}</p>
                </div>
                <div className="flex items-center gap-2.5">
                    <select data-testid="circ-filter-app" value={filterApp} onChange={(e) => setFilterApp(e.target.value)}
                        className="rounded-[10px] border border-slate-300 dark:border-white/15 bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]">
                        <option value="all">{`${tr("All files")} (${items.length})`}</option>
                        {fileNumbers.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <button data-testid="new-circulation-btn" onClick={() => setShowNew(true)} className="brand-btn inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-sm font-bold">
                        <Plus className="w-4 h-4" /> {tr("New circulation")}
                    </button>
                </div>
            </div>

            {/* Summary strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="circ-summary">
                {[
                    { label: tr("In flight"), value: stats.inflight, icon: RefreshCcw, tone: "text-teal", bg: "bg-[#00B4D8]/10", testid: "circ-stat-inflight" },
                    { label: tr("Overdue (>21d)"), value: stats.overdue.length, icon: AlertTriangle, tone: "text-[#EF476F]", bg: "bg-[#EF476F]/10", testid: "circ-stat-overdue", warn: stats.overdue.length > 0 },
                    { label: tr("Avg days out"), value: `${stats.avgDays}d`, icon: Clock, tone: "text-[#B8860B]", bg: "bg-[#FFB703]/12", testid: "circ-stat-avg" },
                    { label: tr("Response rate"), value: `${stats.rate}%`, icon: TrendingUp, tone: "text-[#05A37E]", bg: "bg-[#06D6A0]/12", testid: "circ-stat-rate" },
                ].map((s) => (
                    <div key={s.label} data-testid={s.testid}
                        className={`bg-card rounded-[16px] border p-4 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] flex items-center gap-3.5 ${s.warn ? "border-[#EF476F]/40" : "border-white/70 dark:border-white/10"}`}>
                        <div className={`w-10 h-10 rounded-[12px] ${s.bg} flex items-center justify-center shrink-0`}>
                            <s.icon className={`w-5 h-5 ${s.tone}`} />
                        </div>
                        <div>
                            <div className={`font-data font-bold text-xl ${s.warn ? "text-[#EF476F]" : "text-navy dark:text-white"}`}>{s.value}</div>
                            <div className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-400">{tr(s.label)}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Needs attention */}
            {stats.overdue.length > 0 && (
                <div className="rounded-[16px] border border-[#EF476F]/30 bg-[#EF476F]/[0.06] p-5" data-testid="circ-needs-attention">
                    <div className="flex items-center gap-2 text-sm font-bold text-[#EF476F]">
                        <AlertTriangle className="w-4 h-4" /> {tr("Needs attention")} — {stats.overdue.length} {tr("Overdue").toLowerCase()}{stats.overdue.length > 1 ? "s" : ""}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2.5">
                        {stats.overdue.map((c) => (
                            <div key={c.id} className="inline-flex items-center gap-2.5 bg-card rounded-full border border-[#EF476F]/25 pl-4 pr-1.5 py-1.5 text-xs">
                                <span className="font-data font-semibold text-teal">{c.file_number}</span>
                                <span className="text-slate-600 dark:text-slate-300">{c.recipient}</span>
                                <span className="font-data font-bold text-[#EF476F]">{c.days_outstanding}d</span>
                                <button data-testid={`nudge-${c.id.slice(0, 8)}`} onClick={() => nudge(c)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#EF476F]/10 text-[#EF476F] font-bold hover:bg-[#EF476F]/20 transition-colors">
                                    <Bell className="w-3 h-3" /> {tr("Nudge")}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Kanban */}
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1" data-testid="kanban-board">
                {columns.map((col) => (
                    <div key={col.id}
                        data-testid={`kanban-col-${col.id}`}
                        onDragOver={(e) => { e.preventDefault(); setOverCol(col.id); }}
                        onDragLeave={() => setOverCol((c) => (c === col.id ? null : c))}
                        onDrop={(e) => {
                            e.preventDefault();
                            setOverCol(null);
                            const circ = items.find((i) => i.id === dragId);
                            if (circ) move(circ, col.id);
                            setDragId(null);
                        }}
                        className={`w-[300px] shrink-0 rounded-[18px] bg-white/50 dark:bg-white/[0.04] border border-white/70 dark:border-white/5 p-3 transition-colors ${overCol === col.id ? "kanban-col-dragover" : ""}`}>
                        <div className="px-2 pb-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                                    <span className="text-sm font-bold text-navy dark:text-slate-100">{tr(col.label)}</span>
                                </div>
                                <span className="text-xs font-data font-bold text-slate-400 bg-white/70 dark:bg-white/10 rounded-full px-2 py-0.5" data-testid={`kanban-count-${col.id}`}>{grouped[col.id].length}</span>
                            </div>
                            <div className="text-[10px] text-slate-400 mt-1">{tr(col.hint)}</div>
                        </div>
                        <div className="space-y-2.5 min-h-[80px]">
                            {grouped[col.id].map((c) => {
                                const overdue = c.days_outstanding > OVERDUE_DAYS;
                                const action = NEXT_ACTION[c.status];
                                return (
                                    <div key={c.id}
                                        draggable
                                        onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDragId(c.id); }}
                                        onDragEnd={() => { setDragId(null); setOverCol(null); }}
                                        data-testid={`kanban-card-${c.id.slice(0, 8)}`}
                                        className={`bg-card rounded-[14px] border border-slate-200/70 dark:border-white/10 p-4 shadow-[0_6px_20px_-10px_rgba(10,37,64,0.15)] cursor-grab active:cursor-grabbing hover:shadow-[0_14px_30px_-12px_rgba(10,37,64,0.28)] hover:-translate-y-0.5 transition-all border-l-4 ${dragId === c.id ? "opacity-40 scale-[0.98]" : ""}`}
                                        style={{ borderLeftColor: overdue ? "#EF476F" : col.color }}>
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="inline-flex items-center gap-1.5 min-w-0">
                                                <GripVertical className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 shrink-0" />
                                                <Link to={`/applications/${c.application_id}`} onClick={(e) => e.stopPropagation()}
                                                    className="font-data text-[11px] text-teal font-bold hover:underline">{c.file_number}</Link>
                                            </span>
                                            {c.days_outstanding > 0 && (
                                                <span className={`text-[10px] font-data font-bold px-1.5 py-0.5 rounded ${overdue ? "bg-[#EF476F]/10 text-[#EF476F]" : "bg-slate-100 dark:bg-white/10 text-slate-500"}`}>
                                                    {c.days_outstanding}d
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-2.5 flex items-start gap-2.5">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0" style={{ backgroundColor: col.color }}>
                                                {c.recipient.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-bold text-navy dark:text-slate-100 leading-snug">{c.recipient}</div>
                                                <div className="text-[11px] text-slate-400">{c.organization}</div>
                                                {c.address && <div className="text-[11px] text-slate-400 mt-0.5 truncate">{c.address}</div>}
                                            </div>
                                        </div>
                                        {c.comments && (
                                            <div className="mt-2.5 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-white/5 rounded-[8px] p-2.5 leading-relaxed line-clamp-3">{c.comments}</div>
                                        )}
                                        {action && (
                                            <button
                                                data-testid={`circ-action-${c.id.slice(0, 8)}`}
                                                onClick={() => move(c, action.to)}
                                                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-bold py-2 rounded-[8px] border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-300 hover:border-[#00B4D8] hover:text-[#0092B0] hover:bg-[#00B4D8]/5 transition-colors">
                                                <action.icon className="w-3.5 h-3.5" /> {tr(action.label)}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            {grouped[col.id].length === 0 && (
                                <div className="text-center text-[11px] text-slate-400 py-6 border border-dashed border-slate-200 dark:border-white/10 rounded-[12px]">{tr("Drop cards here")}</div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogContent className="sm:max-w-md rounded-[16px]" data-testid="new-circulation-dialog">
                    <DialogHeader><DialogTitle className="font-heading">{tr("New Circulation")}</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-2">
                        <select data-testid="circ-app-select" value={form.application_id} onChange={(e) => setForm({ ...form, application_id: e.target.value })}
                            className="w-full rounded-[8px] border border-slate-300 dark:border-white/15 bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]">
                            <option value="">{tr("Select application…")}</option>
                            {apps.map((a) => <option key={a.id} value={a.id}>{`${a.file_number} — ${a.title}`}</option>)}
                        </select>
                        <input data-testid="circ-recipient-input" value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })}
                            placeholder={tr("Recipient (e.g. Transportation Division)")}
                            className="w-full rounded-[8px] border border-slate-300 dark:border-white/15 bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
                        <input data-testid="circ-org-input" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
                            placeholder={tr("Organization (e.g. City of Brampton)")}
                            className="w-full rounded-[8px] border border-slate-300 dark:border-white/15 bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
                        <button data-testid="circ-create-btn" onClick={create} disabled={!form.application_id || !form.recipient}
                            className="brand-btn w-full py-3 rounded-[10px] font-bold text-sm disabled:opacity-40">{tr("Add circulation")}</button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
